import {hideLoadingFunction, showLoading} from 'Core/helpers/loading';
import {ioJsonAction, ioJsonManualAction} from 'Core/store/actions/io';
import {getArray, getString, isset} from 'Core/helpers/data';
import * as checkListItemDataMap from './dataMap/checkListItem';
import {actionCreators} from 'Core/store/reducers';
import {reducerStoreKey} from './reducer';
import {ASYNC_DOWNLOAD_CHECK_LIST_ITEM_STATUS} from 'Components/advanced/AsyncDownload/const';
import {addErrorMessage} from 'Core/helpers/message';
import {translate} from 'Core/i18n';
import * as config from './config';

/**
 * Start async download by sending the request to the server
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {AsyncDownloadType} exportType
 * @param {string} exportFilename - Export file name.
 * @param {AsyncDownlaodFormat} exportFormat - Export file format.
 * @param {string} requestParams - Filter params.
 * @param {string} [loadingSelector=''] - Selector for the loading overlay.
 * @return {function(*): Promise<IoJsonResponseObject|undefined>}
 */
export const startAsyncDownloadAction = (
	abortCallback, exportType, exportFilename, exportFormat, requestParams, loadingSelector = ''
) => dispatch => {
	const loading = (loadingSelector ? showLoading(loadingSelector) : null);
	return ioJsonAction(
		abortCallback,
		'defaultAuthorizedApi',
		'export/start-export',
		{
			exportType,
			exportFormat,
			exportFilename,
			customerMessageDeliveryFilter: requestParams,
		},
		(loading ? hideLoadingFunction : undefined),
	)(dispatch);
};

/**
 * Cancels an ongoing asynchronous download action by making an API request.
 * 
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} exportRequestId - ID of the export request to abort.
 * @param {string} [loadingSelector=''] - Selector for the loading overlay.
 * @return {function(*): Promise<IoJsonResponseObject|undefined>}
 */
export const abortAsyncDownloadAction = (abortCallback, exportRequestId, loadingSelector = '') => dispatch => {
	const loading = (loadingSelector ? showLoading(loadingSelector) : null);
	return ioJsonAction(
		abortCallback,
		'defaultAuthorizedApi',
		`export/cancel-export`,
		{exportRequestId},
		(loading ? hideLoadingFunction : undefined),
	)(dispatch);
};

/**
 * Check the status of async downloads.
 * 
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} [loadingSelector=''] - Selector for the loading overlay.
 * @return {function(*): Promise<AsyncDownloadCheckListItemDataObject[]|undefined>}
 */
export const checkAsyncDownloadStatusAction = (abortCallback, loadingSelector) => dispatch => {
	const loading = (loadingSelector ? showLoading(loadingSelector) : null);
	return ioJsonManualAction(
		abortCallback,
		'defaultAuthorizedApi',
		'export/check-exports',
		null,
		(loading ? hideLoadingFunction : undefined),
	)(dispatch)
		.catch(() => undefined)
		// Get mapped data from response data
		.then(res => (isset(res) ? getArray(res, 'data').map(checkListItemDataMap.input) : res))
		// Show global error messages for failed items and remove them from the list since they will only be returned once
		// by the API.
		.then(list => {
			if (typeof list === 'undefined') return list;
			return getArray(list).filter(item => {
				if (item.status === ASYNC_DOWNLOAD_CHECK_LIST_ITEM_STATUS.FAILED) {
					addErrorMessage(
						translate(
							'checkListItemFailed',
							`${config.translationPath}.AsyncDownloadDropdown`,
							{fileName: getString(item, 'fileName')}
						),
						-1
					);
					return false;
				}
				return true;
			});
		})
		// Set async download status to null (loading) in the Redux store, so while there are response errors, GUI will 
		// schedule another check since it relies on the list change.
		.then(list => {
			if (typeof list === 'undefined') loadAsyncDownloadStatusAction(null)(dispatch);
			return list;
		});
};

/**
 * Load the async download status check list into the Redux store.
 * @param {AsyncDownloadCheckListItemDataObject[]|undefined} list
 * @return {(function(*): void)|*}
 */
export const loadAsyncDownloadStatusAction = list => dispatch => {
	dispatch(actionCreators[reducerStoreKey].setAsyncDownloadCheckList(list));
};